import { apiOrders } from "@/api"
import { basicInfoAtom, cartAtom } from "@/atoms"
import { getPaymentIcon } from "@/components/orders/payment-icon"
import { recordStorefrontEvent } from "@/domain/storefront-analytics"
import { Wallet } from "@/icons"
import { CURRENCY_SYMBOL, formatBRL } from "@/lib/format-brl"
import { formatDecimalBR } from "@/lib/format-decimal-br"
import { getMerchantId } from "@/lib/company-storage"
import { PaymentMethod } from "@/types"
import { ApiPaymentMethod } from "@/types/basic-info"
import { PaymentMethodComponent } from "@/types/components"
import { useAtom, useAtomValue } from "jotai"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useCheckoutContext } from "../CheckoutContext"
import { PaymentTab, PaymentTabs } from "../components/PaymentTabs"
import { StepFooter } from "../components/StepFooter"
import { StepHeader } from "../components/StepHeader"
import { useEnforceWizardEntry } from "../hooks/useEnforceWizardEntry"
import { applyOrderTiming, checkoutAtom, isScheduleStepVisible, stepGuards } from "../state"
import { useDiscountPlan } from "../hooks/useDiscountPlan"
import {
  Button,
  Callout,
  Card,
  Field,
  InputGroup,
  PageContainer,
  Price,
  SectionHeader,
  SelectableCard,
  SelectableCardGroup,
  Text,
  TextField,
  segmentPanelId,
} from "@/components/ui"
import { useStorefrontScheduling } from "@/hooks/use-storefront-scheduling"

/**
 * Terms the customer must read before paying a scheduled order up front
 * (answer 14): a no-show is not refunded automatically.
 */
const SCHEDULED_PREPAID_TERMS =
  "Pedido agendado pago pelo app não é estornado automaticamente se não for retirado ou recebido. Cancelamentos feitos pela loja são estornados."

const CARD_TYPE_LABELS: Record<string, string> = {
  VISA: "Visa",
  MASTERCARD: "Mastercard",
  DINERS: "Diners",
  AMEX: "American Express",
  HIPERCARD: "Hipercard",
  ELO: "Elo",
  AURA: "Aura",
  DISCOVER: "Discover",
  VR_BENEFICIOS: "VR Benefícios",
  SODEXO: "Sodexo",
  TICKET: "Ticket",
  GOOD_CARD: "Good Card",
  BANESCARD: "Banescard",
  SOROCARD: "Sorocard",
  POLICARD: "Policard",
  VALECARD: "Valecard",
  AGICARD: "Agicard",
  JCB: "JCB",
  CREDSYSTEM: "Credsystem",
  CABAL: "Cabal",
  GREEN_CARD: "Green Card",
  VEROCHEQUE: "Verocheque",
  AVISTA: "Avista",
  OTHER: "Outro",
}

function mapApiTypeToKind(apiType: string): PaymentMethod {
  switch (apiType.toUpperCase()) {
    case "CREDIT":
      return "CREDIT"
    case "DEBIT":
      return "DEBIT"
    case "PIX":
      return "PIX"
    case "PIX_ONLINE":
      return "PIX_ONLINE"
    case "CASH":
      return "CASH"
    default:
      return "CARD"
  }
}

function buildMethodInfo(pm: ApiPaymentMethod): string {
  const parts: string[] = []
  if (pm.externalCode) parts.push(pm.externalCode)
  parts.push(pm.name)
  return parts.join("|")
}

function maskMoneyInput(raw: string): string {
  let digits = raw.replace(/\D/g, "")
  if (digits.length === 0) return ""
  if (digits.length < 3) digits = digits.padStart(3, "0")
  const intPart = digits.slice(0, -2)
  const decimalPart = digits.slice(-2)
  return `${parseInt(intPart, 10)},${decimalPart}`
}

function parseMaskedMoney(masked: string): number {
  if (!masked) return 0
  return Number(masked.replace(",", "."))
}

/**
 * Tab the step opens on: the one holding the method already chosen, so coming
 * back from a later step lands on the selection; "app" otherwise, to nudge the
 * customer toward Pix Online.
 */
function optionKey(option: PaymentMethodComponent, idx: number): string {
  return option.methodInfo ?? `${option.kind}-${idx}`
}

function initialTab(paymentMethod: PaymentMethod | undefined): PaymentTab {
  return paymentMethod && paymentMethod !== "PIX_ONLINE" ? "delivery" : "app"
}

const CHANGE_FOR_ID = "change-for-input"
const CHANGE_HINT_ID = "change-for-hint"

function ChangeForMessage({ typedAmount, total }: { typedAmount: number; total: number }) {
  if (typedAmount <= 0 || typedAmount === total) {
    return (
      <Text id={CHANGE_HINT_ID} size="base" tone="muted" className="mt-3">
        Sem troco, você vai pagar o valor exato.
      </Text>
    )
  }
  if (typedAmount < total) {
    return (
      <Text id={CHANGE_HINT_ID} size="base" weight="semibold" tone="danger" className="mt-3">
        Faltam {formatBRL(total - typedAmount)} para o total de {formatBRL(total)}.
      </Text>
    )
  }
  return (
    <Text id={CHANGE_HINT_ID} size="base" weight="semibold" tone="secondary" className="mt-3">
      Troco de {formatBRL(typedAmount - total)}.
    </Text>
  )
}

export function PaymentStep() {
  const { addresses, hasExtras } = useCheckoutContext()
  const cart = useAtomValue(cartAtom)
  const basicInfo = useAtomValue(basicInfoAtom)
  const [state, setState] = useAtom(checkoutAtom)
  const plan = useDiscountPlan()
  const navigate = useNavigate()
  const [changeForInput, setChangeForInput] = useState(state.changeFor != null ? formatDecimalBR(state.changeFor) : "")
  const [pixOnlineAvailable, setPixOnlineAvailable] = useState(false)
  const scheduling = useStorefrontScheduling()
  const scheduledPolicy = state.orderTiming === "SCHEDULED" ? state.scheduledPayment : null
  const prepaymentRequired = scheduledPolicy?.prepaymentRequired === true
  const prepaidBlocked = prepaymentRequired && !pixOnlineAvailable

  const [activeTab, setActiveTab] = useState<PaymentTab>(() => initialTab(state.paymentMethod))

  useEnforceWizardEntry("payment", addresses)

  useEffect(() => {
    recordStorefrontEvent({ type: "checkout_step", checkoutStep: "payment" })
  }, [])

  useEffect(() => {
    const merchantId = getMerchantId()
    if (!merchantId) return

    let cancelled = false

    apiOrders
      .get<{
        pixOnlineAvailable?: boolean
        pixOnlinePassFeeToCustomer?: boolean
        pixOnlineServiceFeeMinor?: number
      }>(`/public/merchants/${merchantId}/available-payment-methods`)
      .then(({ data }) => {
        if (cancelled) return
        setPixOnlineAvailable(data.pixOnlineAvailable === true)
        const feeMinor =
          typeof data.pixOnlineServiceFeeMinor === "number" && data.pixOnlineServiceFeeMinor > 0
            ? data.pixOnlineServiceFeeMinor
            : 60
        setState((prev) => ({
          ...prev,
          pixOnlinePassFeeToCustomer: data.pixOnlinePassFeeToCustomer === true,
          pixServiceFee: feeMinor / 100,
        }))
      })
      .catch(() => {})

    return () => {
      cancelled = true
    }
  }, [setState])

  const paymentOptions: PaymentMethodComponent[] = useMemo(() => {
    const apiMethods = basicInfo?.paymentMethods
    if (apiMethods && apiMethods.length > 0) {
      const options: PaymentMethodComponent[] = apiMethods.map((pm) => {
        const kind = mapApiTypeToKind(pm.type)
        return {
          kind,
          text: pm.name,
          icon: getPaymentIcon(kind, undefined, "current"),
          methodInfo: buildMethodInfo(pm),
          paymentMethodId: pm.id,
        }
      })

      const hasPixOnlineFromApi = options.some((o) => o.kind === "PIX_ONLINE")
      if (pixOnlineAvailable && !hasPixOnlineFromApi) {
        options.push({
          kind: "PIX_ONLINE",
          text: "Pix Online",
          icon: getPaymentIcon("PIX_ONLINE", undefined, "current"),
          methodInfo: "PIX_ONLINE",
        })
      }

      return options
    }

    const fallback: PaymentMethodComponent[] = [
      { kind: "CASH", icon: getPaymentIcon("CASH", undefined, "current"), text: "Dinheiro" },
      { kind: "PIX", icon: getPaymentIcon("PIX", undefined, "current"), text: "Pix" },
    ]
    const accepted = basicInfo?.acceptedCards
    if (accepted && accepted.length > 0) {
      for (const card of accepted) {
        fallback.push({
          kind: "CARD",
          text: CARD_TYPE_LABELS[card] ?? card,
          icon: getPaymentIcon("CARD", undefined, "current"),
          methodInfo: card,
        })
      }
    } else {
      fallback.push({
        kind: "CARD",
        icon: getPaymentIcon("CARD", undefined, "current"),
        text: "Cartão na entrega",
      })
    }

    if (pixOnlineAvailable) {
      fallback.push({
        kind: "PIX_ONLINE",
        text: "Pix Online",
        icon: getPaymentIcon("PIX_ONLINE", undefined, "current"),
        methodInfo: "PIX_ONLINE",
      })
    }

    return fallback
  }, [basicInfo?.paymentMethods, basicInfo?.acceptedCards, pixOnlineAvailable])

  const allowedOptions = useMemo(() => {
    if (!scheduledPolicy) return paymentOptions
    return paymentOptions.filter((option) => {
      if (option.kind === "PIX_ONLINE") {
        return scheduledPolicy.prepaidMethods.includes("PIX_ONLINE")
      }
      if (prepaymentRequired) return false
      return option.paymentMethodId ? scheduledPolicy.pendingMethodIds.includes(option.paymentMethodId) : false
    })
  }, [paymentOptions, scheduledPolicy, prepaymentRequired])

  const pixOnlineOptions = useMemo(() => allowedOptions.filter((o) => o.kind === "PIX_ONLINE"), [allowedOptions])
  const deliveryOptions = useMemo(() => allowedOptions.filter((o) => o.kind !== "PIX_ONLINE"), [allowedOptions])

  const showTabs = pixOnlineAvailable && pixOnlineOptions.length > 0 && deliveryOptions.length > 0

  const visibleOptions = useMemo(() => {
    if (!showTabs) return allowedOptions
    return activeTab === "app" ? pixOnlineOptions : deliveryOptions
  }, [showTabs, activeTab, allowedOptions, pixOnlineOptions, deliveryOptions])

  const selectedOptionKey =
    visibleOptions
      .map((option, idx) => ({ option, key: optionKey(option, idx) }))
      .find(({ option }) =>
        option.methodInfo ? state.selectedMethodInfo === option.methodInfo : state.paymentMethod === option.kind
      )?.key ?? null

  function handleTabChange(tab: PaymentTab) {
    setActiveTab(tab)

    const nextOptions = tab === "app" ? pixOnlineOptions : deliveryOptions
    const isSelectedVisible = nextOptions.some((o) =>
      state.selectedMethodInfo ? o.methodInfo === state.selectedMethodInfo : o.kind === state.paymentMethod
    )
    if (!isSelectedVisible) {
      setState((prev) => ({
        ...prev,
        paymentMethod: undefined,
        selectedMethodInfo: undefined,
        selectedPaymentMethodId: undefined,
        changeFor: null,
      }))
      setChangeForInput("")
    }
  }

  const cartTotalForPayment = plan.pricing.total

  function selectMethod(option: PaymentMethodComponent) {
    setState((prev) => ({
      ...prev,
      paymentMethod: option.kind,
      selectedMethodInfo: option.methodInfo,
      selectedPaymentMethodId: option.paymentMethodId,
      changeFor: option.kind === "CASH" ? prev.changeFor : null,
    }))
    if (option.kind !== "CASH") {
      setChangeForInput("")
    }
  }

  function applyChangeFor() {
    const value = parseMaskedMoney(changeForInput)
    if (value === 0) {
      setState((prev) => ({ ...prev, changeFor: null }))
      return
    }
    setState((prev) => ({ ...prev, changeFor: value }))
  }

  function clearChangeFor() {
    setChangeForInput("")
    setState((prev) => ({ ...prev, changeFor: null }))
  }

  const validation = prepaidBlocked
    ? { ok: false, hint: "Pagamento pelo app indisponível agora" }
    : stepGuards.payment({
        state,
        cartTotal: cart.totalPrice,
        orderTotal: cartTotalForPayment,
        hasDeliverableAddress: () => true,
      })

  const isCashSelected = state.paymentMethod === "CASH"
  const scheduleStepVisible = isScheduleStepVisible(
    state,
    scheduling.schedulableChannels.map((channel) => channel.serviceType)
  )

  return (
    <>
      <StepHeader
        title="Como você quer pagar?"
        description={
          prepaymentRequired
            ? "Esta loja cobra os pedidos agendados antecipadamente."
            : "Selecione a forma de pagamento. O pagamento é feito na entrega ou retirada."
        }
      />

      {prepaymentRequired ? (
        <PageContainer as="section" className="mb-3">
          <Callout tone="info">Pedidos agendados nesta loja são pagos antecipadamente pelo Pix.</Callout>
        </PageContainer>
      ) : null}

      {prepaidBlocked ? (
        <PageContainer as="section" className="mb-3 space-y-3">
          <Callout tone="error" title="Pix online indisponível">
            O Pix online está indisponível agora, então não dá para agendar nesta loja neste momento.
          </Callout>
          {scheduling.timingStepVisible ? (
            <Button
              variant="outline"
              fullWidth
              onClick={() => {
                setState((prev) => applyOrderTiming(prev, "INSTANT"))
                navigate("/cart/checkout/service", { replace: true })
              }}
            >
              Pedir agora
            </Button>
          ) : null}
        </PageContainer>
      ) : null}

      <PageContainer as="section" className="space-y-3">
        {showTabs && <PaymentTabs activeTab={activeTab} onTabChange={handleTabChange} />}

        <div
          id={showTabs ? segmentPanelId("payment", activeTab) : undefined}
          role={showTabs ? "tabpanel" : undefined}
          aria-labelledby={showTabs ? `payment-tab-${activeTab}` : undefined}
        >
          <SelectableCardGroup
            label="Forma de pagamento"
            value={selectedOptionKey}
            onValueChange={(key) => {
              const next = visibleOptions.find((option, idx) => optionKey(option, idx) === key)
              if (next) selectMethod(next)
            }}
            className="gap-3"
          >
            {visibleOptions.map((option, idx) => (
              <SelectableCard
                key={optionKey(option, idx)}
                value={optionKey(option, idx)}
                icon={option.icon}
                title={option.text}
                tone="secondary"
                density="sm"
                wrapTitle
              />
            ))}
          </SelectableCardGroup>
        </div>
      </PageContainer>

      {state.orderTiming === "SCHEDULED" ? (
        <PageContainer as="section" className="mt-6">
          <Callout tone="warning" title="Antes de agendar">
            {SCHEDULED_PREPAID_TERMS}
          </Callout>
        </PageContainer>
      ) : null}

      {isCashSelected ? (
        <PageContainer as="section" className="mt-8">
          <Card padding="lg">
            <SectionHeader title="Precisa de troco?" icon={Wallet} iconSize="md" />
            <Text size="base" tone="muted" className="mb-4">
              Seu pedido tem o total de <Price value={cartTotalForPayment} weight="bold" tone="default" />. Se você quer
              troco, informe o valor que vai entregar em dinheiro.
            </Text>
            <Field label="Troco para (opcional)" id={CHANGE_FOR_ID}>
              <InputGroup>
                <TextField
                  inputSize="lg"
                  prefix={CURRENCY_SYMBOL}
                  aria-describedby={CHANGE_HINT_ID}
                  placeholder="50,00"
                  value={changeForInput}
                  onChange={(event) => {
                    if (Number(event.target.value.replace(/\D/g, "")) > 100000) return
                    const masked = maskMoneyInput(event.target.value)
                    setChangeForInput(masked)
                    const value = parseMaskedMoney(masked)
                    setState((prev) => ({ ...prev, changeFor: value === 0 ? null : value }))
                  }}
                  onBlur={applyChangeFor}
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={10}
                />
                {state.changeFor != null ? (
                  <Button variant="outline" tone="neutral" size="lg" onClick={clearChangeFor}>
                    Limpar
                  </Button>
                ) : null}
              </InputGroup>
            </Field>
            <ChangeForMessage typedAmount={parseMaskedMoney(changeForInput)} total={cartTotalForPayment} />
          </Card>
        </PageContainer>
      ) : null}

      <StepFooter
        onBack={() =>
          navigate(scheduleStepVisible ? "/cart/checkout/schedule" : "/cart/checkout/service", {
            replace: true,
          })
        }
        onContinue={() => {
          applyChangeFor()
          navigate(hasExtras ? "/cart/checkout/extras" : "/cart/checkout/review")
        }}
        continueDisabled={!validation.ok}
        continueHint={validation.hint}
      />
    </>
  )
}
